import {
  modifyTalkFlow,
  useAiSummeryResult,
  useAiSummeryStatus,
  useTalkFlowDocument,
  useTalkFlowDocumentAiSummary,
} from '@/hooks/apiHooks';
import { useCallback, useEffect, useRef } from 'react';
import { CreateTalkFlowParams, RecordFilter } from '@/types/api';
import {
  getTalkFlowDisplaySubject,
  responseToFilter,
} from '@/components/Record/util/function';
import { UseFormReturn } from 'react-hook-form';
import {
  ensureLexicalHtml,
  getLexicalNodesFromMarkdown,
} from '@/components/Document/@hooks/useAiSummaries';
import { apiURL } from '@/constant/apiURL';
import { useSWRConfig } from 'swr';
import dayjs from 'dayjs';
import { useAtomValue, useSetAtom } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import {
  DocumentAiSummariesData,
  DocumentAiSummariesState,
} from '@/recoil/atom/documents';
import { useTranslation } from 'react-i18next';
import { INIT_RECORD_STATE } from '@/recoil/init_data';
import { TalkFlowAISummary } from '@/types/talk_flow';

// 회의록 내용과 AI summary 내용을 같이 가져오는 hook
const useDocumentData = (
  documentId: string,
  { reset: formReset }: UseFormReturn<RecordFilter, any, undefined>,
) => {
  const { mutate } = useSWRConfig();
  const { passportId } = useAtomValue(MyInfoState);
  const { t } = useTranslation();

  const {
    data: documentData,
    isLoading: documentLoading,
    error: documentError,
    mutate: updateDocument,
  } = useTalkFlowDocument(documentId, { revalidateOnFocus: false });

  const {
    data: aiSummaryData,
    isLoading: aiSummaryLoading,
    error: aiSummaryError,
  } = useTalkFlowDocumentAiSummary(
    { documentId, passportId },
    { revalidateOnFocus: false },
  );

  const updateAiSummaries = useSetAtom(DocumentAiSummariesState);

  const initializedRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (initializedRef.current === documentId) {
      return;
    }

    const init = async () => {
      if (!documentData || aiSummaryData === undefined || cancelled) {
        return;
      }

      // 초기화 시작했음을 즉시 표시 (중복 실행 방지)
      initializedRef.current = documentId;

      const filter = responseToFilter(documentData);
      filter.content = await ensureLexicalHtml(filter.content);
      formReset(filter);

      const initialSummaries = await buildAiSummaries(aiSummaryData, {});
      updateAiSummaries(initialSummaries);

      // content가 없는 항목들의 실제 상태를 API로 확인
      const needsStatusCheck = aiSummaryData.filter(
        (item) => !item.content || item.content.trim() === '',
      );

      if (needsStatusCheck.length > 0 && !cancelled) {
        // 각 항목의 status를 병렬로 조회하고, done인 경우 content도 가져오기
        const statusChecks = needsStatusCheck.map(async (item) => {
          try {
            const statusRes = await useAiSummeryStatus(item.pipelineId);
            const status = statusRes.status;

            // status가 done이면 실제 content도 가져오기
            if (status === 'done') {
              try {
                const contentRes = await useAiSummeryResult(item.pipelineId);
                const rawText = contentRes?.result?.summarize_text ?? '';
                const contentDom = rawText
                  ? await getLexicalNodesFromMarkdown(rawText)
                  : '';

                return {
                  pipelineId: item.pipelineId,
                  status,
                  content: rawText,
                  contentDom,
                };
              } catch (e) {
                console.warn(
                  `[useDocumentData] Failed to fetch content for ${item.pipelineId}:`,
                  e,
                );
                // content 가져오기 실패해도 status는 업데이트
                return { pipelineId: item.pipelineId, status };
              }
            }

            return { pipelineId: item.pipelineId, status };
          } catch (e) {
            console.warn(
              `[useDocumentData] Failed to fetch status for ${item.pipelineId}:`,
              e,
            );
            return null;
          }
        });

        const results = await Promise.all(statusChecks);

        if (!cancelled) {
          updateAiSummaries((prev) => {
            const next = { ...prev };
            let changed = false;

            for (const result of results) {
              if (!result || !next[result.pipelineId]) {
                continue;
              }

              const { pipelineId, status, content, contentDom } = result;
              let newUiState = next[pipelineId].uiState;

              // 서버 status를 UI state로 변환
              switch (status) {
                case 'done':
                  newUiState = 'completed';
                  break;
                case 'in_progress':
                  newUiState = 'loading';
                  break;
                case 'error_done':
                  newUiState = 'error';
                  break;
                case 'failover_done':
                case 'validation_error_done':
                  newUiState = 'validation_error';
                  break;
              }

              // uiState 또는 content가 변경되었으면 업데이트
              if (
                next[pipelineId].uiState !== newUiState ||
                (content && next[pipelineId].content !== content)
              ) {
                next[pipelineId] = {
                  ...next[pipelineId],
                  uiState: newUiState,
                  content: content || next[pipelineId].content,
                  contentDom: contentDom || next[pipelineId].contentDom,
                };
                changed = true;
              }
            }

            return changed ? next : prev;
          });
        }
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
  }, [documentId, documentData, aiSummaryData, formReset]);

  const initialize = useCallback(() => {
    initializedRef.current = null;
    updateAiSummaries({});
    formReset(INIT_RECORD_STATE);
  }, [formReset, formReset]);

  const refresh = useCallback(async () => {
    const DOC_KEY = documentId
      ? apiURL.TALK_FLOW_DOCUMENT_BY_ID(documentId)
      : null;

    const AI_KEY = documentId
      ? apiURL.AI_PIPELINE_RESULT_DETAIL(documentId, passportId)
      : null;

    await Promise.all([
      DOC_KEY
        ? mutate(DOC_KEY, undefined, { revalidate: true })
        : Promise.resolve(),
      AI_KEY
        ? mutate(AI_KEY, undefined, { revalidate: true })
        : Promise.resolve(),
    ]);
  }, [documentId, mutate, passportId]);

  const editDocument = useCallback(
    async (payload: CreateTalkFlowParams) => {
      await modifyTalkFlow(payload, documentId);
      await updateDocument();
    },
    [documentId],
  );

  return {
    initialize,
    isLoading: documentLoading || aiSummaryLoading || !documentData,
    error: documentError || aiSummaryError,
    pageHead: getTalkFlowDisplaySubject(
      documentData?.subject,
      documentData?.suggestedSubject,
      t('상세'),
    ),
    refresh,
    editDocument,
    documentLoading,
  };
};

export default useDocumentData;

async function buildAiSummaries(
  list: TalkFlowAISummary[],
  existingStates: DocumentAiSummariesData,
): Promise<DocumentAiSummariesData> {
  const entries = await Promise.all(
    [...list]
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      .map(
        async ({
          pipelineId,
          prompt,
          content = '',
          createdAt,
          summarizeType = 'both',
        }) => {
          const contentDom = content
            ? await getLexicalNodesFromMarkdown(content)
            : '';

          // 기존 uiState가 있으면 유지, 없으면 초기 설정
          let uiState = existingStates[pipelineId]?.uiState;

          if (!uiState) {
            // 초기 로드 시 uiState 설정 (content 있으면 completed, 없으면 loading)
            uiState = content ? 'completed' : 'loading';
          }

          return [
            pipelineId,
            {
              id: pipelineId,
              prompt,
              content,
              contentDom,
              createdAt: dayjs(createdAt),
              uiState,
              summarizeType,
            },
          ] as const;
        },
      ),
  );
  return Object.fromEntries(entries) as DocumentAiSummariesData;
}
